import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle, Circle, BookOpen, Trophy, RotateCcw, Eye, EyeOff } from 'lucide-react';
import MathExpression from '../MathExpression';
import PDFControls from '../PDFControls';

const ExerciseInterface = ({ 
  moduleInfo, 
  topics = [], 
  problems = [], 
  onProgress = () => {},
  className = "" 
}) => {
  const [currentTopic, setCurrentTopic] = useState(0);
  const [currentProblem, setCurrentProblem] = useState(0);
  const [showSolution, setShowSolution] = useState(false);
  const [completedProblems, setCompletedProblems] = useState({});
  const [userAnswer, setUserAnswer] = useState('');

  // 載入進度
  useEffect(() => {
    const saved = localStorage.getItem(`mathProgress_${moduleInfo.moduleId}`);
    if (saved) {
      setCompletedProblems(JSON.parse(saved));
    }
  }, [moduleInfo.moduleId]);

  // 觸發數學表達式渲染
  useEffect(() => {
    if (window.MathJax && window.MathJax.typesetPromise) {
      window.MathJax.typesetPromise().catch((err) => {
        console.warn('MathJax rendering error:', err);
      });
    }
  }, [currentTopic, currentProblem, showSolution]);

  // 保存進度
  const saveProgress = (topicIndex, problemIndex) => {
    const problemData = getCurrentProblemData();
    if (!problemData.id || !moduleInfo?.moduleId) return;
    
    const problemId = problemData.id;
    const newCompleted = {
      ...completedProblems,
      [problemId]: true
    };
    setCompletedProblems(newCompleted);
    localStorage.setItem(`mathProgress_${moduleInfo.moduleId}`, JSON.stringify(newCompleted));
    onProgress(newCompleted);
  };

  // 計算總進度
  const getTotalProgress = () => {
    if (!problems || !Array.isArray(problems)) return 0;
    const totalProblems = problems.length;
    const completedCount = Object.keys(completedProblems).length;
    return totalProblems > 0 ? Math.round((completedCount / totalProblems) * 100) : 0;
  };

  // 計算主題進度
  const getTopicProgress = (topicIndex) => {
    if (!topics || !problems || !Array.isArray(topics) || !Array.isArray(problems)) return 0;
    const topic = topics[topicIndex];
    if (!topic) return 0;
    const topicProblems = problems.filter(p => p.topicId === topic.id);
    const completedCount = topicProblems.reduce((count, problem) => {
      return completedProblems[problem.id] ? count + 1 : count;
    }, 0);
    return topicProblems.length > 0 ? Math.round((completedCount / topicProblems.length) * 100) : 0;
  };

  // 獲取當前主題的問題
  const getCurrentTopicProblems = () => {
    if (!topics || !problems || !Array.isArray(topics) || !Array.isArray(problems)) return [];
    const topic = topics[currentTopic];
    if (!topic) return [];
    return problems.filter(p => p.topicId === topic.id).sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // 獲取當前問題數據
  const getCurrentProblemData = () => {
    const topicProblems = getCurrentTopicProblems();
    return topicProblems[currentProblem] || {};
  };

  const currentTopicData = topics && topics[currentTopic] ? topics[currentTopic] : null;
  const currentProblemData = getCurrentProblemData();
  const currentTopicProblems = getCurrentTopicProblems();
  const isProblemCompleted = currentProblemData.id ? completedProblems[currentProblemData.id] : false;

  // 導航到下一個問題
  const navigateNext = () => {
    if (!topics || !Array.isArray(topics) || topics.length === 0) return;
    
    if (currentProblem < currentTopicProblems.length - 1) {
      setCurrentProblem(currentProblem + 1);
      setShowSolution(false);
      setUserAnswer('');
    } else if (currentTopic < topics.length - 1) {
      setCurrentTopic(currentTopic + 1);
      setCurrentProblem(0);
      setShowSolution(false);
      setUserAnswer('');
    }
  };

  // 導航到上一個問題
  const navigatePrevious = () => {
    if (!topics || !problems || !Array.isArray(topics) || !Array.isArray(problems) || topics.length === 0) return;
    
    if (currentProblem > 0) {
      setCurrentProblem(currentProblem - 1);
      setShowSolution(false);
      setUserAnswer('');
    } else if (currentTopic > 0) {
      const prevTopic = currentTopic - 1;
      const prevTopicData = topics[prevTopic];
      if (prevTopicData) {
        const prevTopicProblems = problems.filter(p => p.topicId === prevTopicData.id);
        setCurrentTopic(prevTopic);
        setCurrentProblem(Math.max(0, prevTopicProblems.length - 1));
        setShowSolution(false);
        setUserAnswer('');
      }
    }
  };

  // 如果沒有數據，顯示加載或錯誤狀態
  if (!moduleInfo || !topics || !problems || !Array.isArray(topics) || !Array.isArray(problems)) {
    return (
      <div className={`bg-gradient-to-br from-blue-50 to-indigo-100 p-4 pb-8 ${className}`}>
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-blue-500" />
            <h3 className="text-xl font-semibold mb-2">載入練習系統中...</h3>
            <p className="text-gray-600">請稍候，正在準備練習內容</p>
          </div>
        </div>
      </div>
    );
  }

  // 如果沒有主題或問題，顯示空狀態
  if (topics.length === 0 || problems.length === 0) {
    return (
      <div className={`bg-gradient-to-br from-blue-50 to-indigo-100 p-4 pb-8 ${className}`}>
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">暫無練習內容</h3>
            <p className="text-gray-600">該模組的練習內容正在準備中</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br from-blue-50 to-indigo-100 p-4 pb-8 ${className}`}>
      <div className="max-w-6xl mx-auto">
        {/* 標題和總進度 */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-center text-indigo-800 mb-4">
            數學學習系統 - {moduleInfo?.title || '練習系統'}
          </h1>
          <div className="flex items-center justify-center gap-4">
            <Trophy className="text-yellow-500" size={24} />
            <div className="bg-gray-200 rounded-full w-64 h-3">
              <div 
                className="bg-gradient-to-r from-green-400 to-blue-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${getTotalProgress()}%` }}
              ></div>
            </div>
            <span className="font-semibold text-lg">{getTotalProgress()}%</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* 側邊欄 - 主題列表 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-4">
              <h3 className="font-bold text-lg mb-4 text-indigo-700">學習主題</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {topics && topics.map((topic, index) => (
                  <div
                    key={topic.id}
                    onClick={() => {
                      setCurrentTopic(index);
                      setCurrentProblem(0);
                      setShowSolution(false);
                      setUserAnswer('');
                    }}
                    className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      currentTopic === index 
                        ? 'bg-indigo-100 border-2 border-indigo-300' 
                        : 'bg-gray-50 hover:bg-indigo-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${
                        currentTopic === index 
                          ? 'text-indigo-800' 
                          : 'text-gray-800'
                      }`}>{index + 1}. {topic.title || topic.name}</span>
                      <span className="text-xs text-gray-600">{getTopicProgress(index)}%</span>
                    </div>
                    <div className="mt-1 bg-gray-200 rounded-full h-1">
                      <div 
                        className="bg-indigo-400 h-1 rounded-full transition-all duration-300"
                        style={{ width: `${getTopicProgress(index)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 主要內容區 */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-lg p-6">
              {/* 主題標題和進度 */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-indigo-800">
                  {currentTopic + 1}. {currentTopicData?.name}
                </h2>
                <div className="flex items-center gap-2">
                  <BookOpen size={20} className="text-indigo-600" />
                  <span className="text-sm text-gray-600">
                    {currentProblem + 1} / {currentTopicProblems.length}
                  </span>
                </div>
              </div>

              {/* 問題導航 */}
              <div className="flex flex-wrap gap-2 mb-6">
                {currentTopicProblems && currentTopicProblems.map((problem, index) => (
                  <button
                    key={problem.id}
                    onClick={() => {
                      setCurrentProblem(index);
                      setShowSolution(false);
                      setUserAnswer('');
                    }}
                    className={`w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                      currentProblem === index
                        ? 'bg-indigo-500 text-white border-indigo-500'
                        : completedProblems[problem.id]
                        ? 'bg-green-100 text-green-800 border-green-300'
                        : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-indigo-100'
                    }`}
                  >
                    {completedProblems[problem.id] ? (
                      <CheckCircle size={16} className="mx-auto" />
                    ) : (
                      index + 1
                    )}
                  </button>
                ))}
              </div>

              {/* PDF控制項 */}
              <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <h4 className="text-sm font-semibold text-indigo-800">PDF 生成工具</h4>
                </div>
                <PDFControls
                  currentQuestion={currentProblemData}
                  currentQuestionNumber={currentProblem + 1}
                  allTopicQuestions={currentTopicProblems}
                  topicInfo={currentTopicData}
                  moduleInfo={moduleInfo}
                  className="w-full"
                />
              </div>

              {/* 問題內容 */}
              {(currentProblemData.question || currentProblemData.question?.text || currentProblemData.parts) && (
                <>
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        問題 {currentProblem + 1}:
                      </h3>
                      <div className="flex items-center gap-2">
                        {currentProblemData.difficulty && (
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            currentProblemData.difficulty === 'basic' ? 'bg-green-100 text-green-800' :
                            currentProblemData.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {currentProblemData.difficulty === 'basic' ? '基礎' :
                             currentProblemData.difficulty === 'intermediate' ? '中級' : '高級'}
                          </span>
                        )}
                        {currentProblemData.points && (
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            {currentProblemData.points}分
                          </span>
                        )}
                        {currentProblemData.timeLimit && (
                          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                            {Math.floor(currentProblemData.timeLimit / 60)}分鐘
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Check if this is a multi-part question */}
                    {currentProblemData.parts ? (
                      /* M07 format with parts object */
                      <div className="space-y-3">
                        <p className="text-xl text-gray-700 leading-relaxed mb-4">
                          <MathExpression>{currentProblemData.question}</MathExpression>
                        </p>
                        
                        {/* Render multi-part questions */}
                        <div className="space-y-6">
                          {['a', 'b', 'c'].map(partKey => {
                            const part = currentProblemData.parts[partKey];
                            if (!part) return null;

                            return (
                              <div key={partKey} className="bg-white rounded-lg p-4 border border-gray-200">
                                <div className="flex items-start gap-3">
                                  <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full min-w-[24px] text-center">
                                    {partKey}
                                  </span>
                                  <div className="flex-1">
                                    <div className="mb-3">
                                      <p className="text-gray-800 font-medium mb-2">題目：</p>
                                      <div className="text-gray-700 mb-3 math-content">
                                        <MathExpression>{part.question}</MathExpression>
                                      </div>
                                    </div>

                                    {part.hint && (
                                      <div className="mb-3 p-3 bg-yellow-50 border-l-4 border-yellow-400">
                                        <p className="text-sm text-yellow-800">
                                          <span className="font-medium">提示：</span>
                                          <span className="ml-1">
                                            <MathExpression>{part.hint}</MathExpression>
                                          </span>
                                        </p>
                                      </div>
                                    )}

                                    {showSolution && part.solution && (
                                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                        <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                                          <CheckCircle size={16} />
                                          解答過程：
                                        </h4>
                                        {part.solution.steps && (
                                          <ol className="list-decimal list-inside space-y-2 text-gray-700">
                                            {part.solution.steps.map((step, index) => (
                                              <li key={index} className="text-sm leading-relaxed">
                                                <MathExpression>{step}</MathExpression>
                                              </li>
                                            ))}
                                          </ol>
                                        )}
                                        
                                        {part.solution.answer && (
                                          <div className="mt-4 p-3 bg-white border border-green-300 rounded">
                                            <p className="text-sm font-medium text-green-800 mb-1">最終答案：</p>
                                            <div className="text-green-700 font-medium math-content">
                                              <MathExpression>{part.solution.answer}</MathExpression>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : currentProblemData.question && currentProblemData.question.includes('\n(a)') ? (
                      /* M04/M05 format with embedded parts - parse and display like M07 */
                      (() => {
                        // Parse the embedded parts from question text
                        const questionText = currentProblemData.question;
                        const parts = {};
                        
                        // Split by main question and parts
                        const mainQuestion = questionText.split('\n(a)')[0];
                        
                        // Extract parts (a), (b), (c) - improved parsing
                        const partRegex = /\n\(([abc])\)\s*([^(\n]*(?:\n(?!\([abc]\))[^(\n]*)*)/g;
                        let match;
                        while ((match = partRegex.exec(questionText)) !== null) {
                          const [, partKey, partQuestion] = match;
                          parts[partKey] = {
                            question: partQuestion.trim(),
                            hint: currentProblemData.hint // Use general hint for all parts
                          };
                        }
                        
                        // Parse solutions from steps
                        if (currentProblemData.steps) {
                          currentProblemData.steps.forEach(step => {
                            const stepMatch = step.match(/^\(([abc])\)\s*(.*)/s);
                            if (stepMatch) {
                              const [, partKey, solution] = stepMatch;
                              if (parts[partKey]) {
                                if (!parts[partKey].solution) {
                                  parts[partKey].solution = { steps: [], answer: '' };
                                }
                                parts[partKey].solution.steps.push(solution.trim());
                              }
                            }
                          });
                        }
                        
                        // Parse final answers from solution field
                        if (currentProblemData.solution) {
                          const solutionParts = currentProblemData.solution.split(', ');
                          solutionParts.forEach(part => {
                            const partMatch = part.match(/\(([abc])\)\s*(.*)/);
                            if (partMatch) {
                              const [, partKey, answer] = partMatch;
                              if (parts[partKey] && parts[partKey].solution) {
                                parts[partKey].solution.answer = answer.trim();
                              }
                            }
                          });
                        }

                        return (
                          <div className="space-y-3">
                            <p className="text-xl text-gray-700 leading-relaxed mb-4">
                              <MathExpression>{mainQuestion}</MathExpression>
                            </p>
                            
                            {/* Render parsed parts like M07 format */}
                            <div className="space-y-6">
                              {['a', 'b', 'c'].map(partKey => {
                                const part = parts[partKey];
                                if (!part) return null;

                                return (
                                  <div key={partKey} className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="flex items-start gap-3">
                                      <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded-full min-w-[24px] text-center">
                                        {partKey}
                                      </span>
                                      <div className="flex-1">
                                        <div className="mb-3">
                                          <p className="text-gray-800 font-medium mb-2">題目：</p>
                                          <div className="text-gray-700 mb-3 math-content">
                                            <MathExpression>{part.question}</MathExpression>
                                          </div>
                                        </div>

                                        {part.hint && (
                                          <div className="mb-3 p-3 bg-yellow-50 border-l-4 border-yellow-400">
                                            <p className="text-sm text-yellow-800">
                                              <span className="font-medium">提示：</span>
                                              <span className="ml-1">
                                                <MathExpression>{part.hint}</MathExpression>
                                              </span>
                                            </p>
                                          </div>
                                        )}

                                        {showSolution && part.solution && (
                                          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                            <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                                              <CheckCircle size={16} />
                                              解答過程：
                                            </h4>
                                            {part.solution.steps && part.solution.steps.length > 0 && (
                                              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                                                {part.solution.steps.map((step, index) => (
                                                  <li key={index} className="text-sm leading-relaxed">
                                                    <MathExpression>{step}</MathExpression>
                                                  </li>
                                                ))}
                                              </ol>
                                            )}
                                            
                                            {part.solution.answer && (
                                              <div className="mt-4 p-3 bg-white border border-green-300 rounded">
                                                <p className="text-sm font-medium text-green-800 mb-1">最終答案：</p>
                                                <div className="text-green-700 font-medium math-content">
                                                  <MathExpression>{part.solution.answer}</MathExpression>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      /* Single-part question rendering */
                      <div className="space-y-3">
                        <p className="text-xl text-gray-700 leading-relaxed">
                          <MathExpression>
                            {typeof currentProblemData.question === 'string' 
                              ? currentProblemData.question 
                              : currentProblemData.question?.text}
                          </MathExpression>
                        </p>
                        {currentProblemData.question?.mathExpression && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-lg text-blue-800 font-medium">
                              <MathExpression>{currentProblemData.question.mathExpression}</MathExpression>
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Answer input - only show for true single-part questions */}
                  {!currentProblemData.parts && !(currentProblemData.question && currentProblemData.question.includes('\n(a)')) && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        您的答案:
                      </label>
                      <input
                        type="text"
                        value={userAnswer}
                        onChange={(e) => setUserAnswer(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="請輸入您的答案..."
                      />
                    </div>
                  )}

                  {/* 按鈕區 */}
                  <div className="flex flex-wrap gap-3 mb-6">
                    <button
                      onClick={() => setShowSolution(!showSolution)}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                    >
                      {showSolution ? <EyeOff size={16} /> : <Eye size={16} />}
                      {showSolution ? '隱藏解答' : '顯示解答'}
                    </button>
                    
                    <button
                      onClick={() => saveProgress(currentTopic, currentProblem)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        isProblemCompleted 
                          ? 'bg-green-500 text-white' 
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      <CheckCircle size={16} />
                      {isProblemCompleted ? '已完成' : '標記完成'}
                    </button>

                    <button
                      onClick={() => {
                        setUserAnswer('');
                        setShowSolution(false);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      <RotateCcw size={16} />
                      重置
                    </button>
                  </div>

                  {/* 解答區 - only show for true single-part questions */}
                  {showSolution && currentProblemData.solution && !currentProblemData.parts && !(currentProblemData.question && currentProblemData.question.includes('\n(a)')) && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-green-800 mb-3">解答:</h4>
                      
                      {/* 處理複雜解答結構 */}
                      {typeof currentProblemData.solution === 'object' ? (
                        <div className="space-y-4">
                          {/* 解題步驟 */}
                          {currentProblemData.solution.steps && (
                            <div>
                              <h5 className="text-md font-semibold text-green-800 mb-2">解題步驟:</h5>
                              <ol className="list-decimal list-inside space-y-1 text-green-700">
                                {currentProblemData.solution.steps.map((step, index) => (
                                  <li key={index} className="text-sm">
                                    <MathExpression>{step}</MathExpression>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          )}
                          
                          {/* 最終答案 */}
                          {currentProblemData.solution.finalAnswer && (
                            <div>
                              <h5 className="text-md font-semibold text-green-800 mb-2">最終答案:</h5>
                              <p className="text-xl font-bold text-green-700 bg-green-100 p-2 rounded">
                                <MathExpression>{currentProblemData.solution.finalAnswer}</MathExpression>
                              </p>
                            </div>
                          )}
                          
                          {/* 解釋說明 */}
                          {currentProblemData.solution.explanation && (
                            <div>
                              <h5 className="text-md font-semibold text-green-800 mb-2">解釋:</h5>
                              <p className="text-green-700 leading-relaxed">
                                <MathExpression>{currentProblemData.solution.explanation}</MathExpression>
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* 簡單解答結構 */
                        <div className="space-y-3">
                          <p className="text-xl font-bold text-green-700 mb-3">
                            <MathExpression>{currentProblemData.solution}</MathExpression>
                          </p>
                          {currentProblemData.explanation && (
                            <>
                              <h5 className="text-md font-semibold text-green-800 mb-2">解釋:</h5>
                              <p className="text-green-700 leading-relaxed">
                                <MathExpression>{currentProblemData.explanation}</MathExpression>
                              </p>
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* 學習提示 */}
                      {currentProblemData.hint && (
                        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <h5 className="text-md font-semibold text-yellow-800 mb-2">💡 提示:</h5>
                          <p className="text-yellow-700 text-sm">
                            <MathExpression>{currentProblemData.hint}</MathExpression>
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 導航按鈕 */}
                  <div className="flex justify-between mt-6">
                    <button
                      onClick={navigatePrevious}
                      disabled={currentTopic === 0 && currentProblem === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={16} />
                      上一題
                    </button>

                    <button
                      onClick={navigateNext}
                      disabled={currentTopic === topics.length - 1 && currentProblem === currentTopicProblems.length - 1}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      下一題
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 統計信息 */}
        <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
          <h3 className="text-xl font-bold text-center text-indigo-800 mb-4">學習統計</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">
                {Object.keys(completedProblems).length}
              </div>
              <div className="text-sm text-gray-600">已完成題目</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">
                {problems ? problems.length : 0}
              </div>
              <div className="text-sm text-gray-600">總題目數</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">
                {topics ? topics.filter((_, index) => getTopicProgress(index) === 100).length : 0}
              </div>
              <div className="text-sm text-gray-600">完成主題</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">
                {topics ? topics.length : 0}
              </div>
              <div className="text-sm text-gray-600">總主題數</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseInterface; 